import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import { DuelChipStackPng, DuelIconClockSvg } from '../../../assets/duels';
import { localizeTableName } from '../../../utils/helpers/localizeTableName';
import { GradientFill } from '../common/GradientFill';
import { Pressable } from '../common/Touchable';
import {
	MY_TABLES_GOLD_FILL,
	myTablesColors,
	tableImage,
	useMyTablesBarSummary,
} from '../MyTables/myTablesShared';

// Figma "Recent Tables Bar" (1625:4559) sets the size: 600x82 on the 1280 frame, stretching with
// the left column (1046 wide at 1920) while its contents keep their size, so the status takes up
// the slack. Its content and colours follow the mobile "My Active & Recent Tables" bar: the
// overline over a row of round seats, the status line, and the gold View Tables button.
export const RECENT_TABLES_BAR_HEIGHT = 82;
const SEAT = 36;
const BUTTON = { width: 132, height: 44 };

type Props = {
	width: number;
	/** Design-to-pixel factor. */
	scale: number;
	onPress: () => void;
};

export const DesktopRecentTablesBar = ({ width, scale, onPress }: Props) => {
	const { t } = useTranslation();
	const s = (v: number) => v * scale;
	const styles = createStyles(s, width);
	const summary = useMyTablesBarSummary();

	return (
		<Pressable onPress={onPress} style={styles.bar}>
			<View style={styles.tables}>
				<Text numberOfLines={1} style={styles.overline}>
					{t('T_SITE_MY_TABLES_BAR_TITLE', { defaultValue: 'My active & recent tables' })}
				</Text>
				<View style={styles.seats}>
					{summary.kind === 'empty' && (
						<Image source={DuelChipStackPng} resizeMode='contain' style={styles.emptyImage} />
					)}
					{summary.seats.map(seat =>
						summary.kind === 'active' ? (
							<View key={seat.key} style={[styles.seat, styles.nameSeat]}>
								<Text numberOfLines={2} style={styles.seatName}>
									{localizeTableName(seat.rawName).replace(/\s+(\S+)$/, '\n$1')}
								</Text>
							</View>
						) : (
							<View key={seat.key} style={styles.seat}>
								<Image
									source={tableImage(seat.rawName)}
									resizeMode='cover'
									style={styles.seatImage}
								/>
							</View>
						),
					)}
					{(summary.kind === 'active' || summary.overflow > 0) && (
						<View
							style={[
								styles.seat,
								styles.overflow,
								summary.kind === 'active' && styles.overflowDashed,
							]}
						>
							<Text
								style={[styles.overflowText, summary.kind === 'active' && styles.overflowTextDim]}
							>
								+{summary.overflow}
							</Text>
						</View>
					)}
				</View>
			</View>

			<View style={styles.messageColumn}>
				<View style={styles.messageRow}>
					{summary.kind === 'active' && <View style={styles.activeDot} />}
					{summary.kind === 'recent' && <DuelIconClockSvg width={s(18)} height={s(18)} />}
					<Text numberOfLines={1} style={styles.message}>
						{summary.message}
					</Text>
				</View>
				{!!summary.detail && (
					<Text numberOfLines={1} style={styles.detail}>
						{summary.detail}
					</Text>
				)}
			</View>

			<View style={styles.viewButton}>
				<GradientFill stops={MY_TABLES_GOLD_FILL} radius={s(10)} />
				<Text numberOfLines={1} adjustsFontSizeToFit style={styles.viewLabel}>
					{t('T_SITE_MY_TABLES_VIEW', { defaultValue: 'View tables' })}
				</Text>
			</View>
		</Pressable>
	);
};

const createStyles = (s: (v: number) => number, width: number) =>
	StyleSheet.create({
		bar: {
			width,
			height: s(RECENT_TABLES_BAR_HEIGHT),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			paddingLeft: s(20),
			paddingRight: s(18),
			borderRadius: s(18),
			borderWidth: Math.max(1, s(1)),
			borderColor: myTablesColors.rim,
			backgroundColor: myTablesColors.panel,
			boxShadow: `0 0 ${s(24)}px rgba(255,0,0,0.35)`,
			cursor: 'pointer',
		} as any,
		tables: {
			rowGap: s(5),
		},
		overline: {
			fontFamily: 'Sofia Sans',
			fontWeight: '800',
			fontSize: s(12),
			lineHeight: s(15),
			letterSpacing: s(0.6),
			color: myTablesColors.gold,
			textTransform: 'uppercase',
		},
		seats: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(5),
		},
		seat: {
			width: s(SEAT),
			height: s(SEAT),
			borderRadius: s(SEAT / 2),
			backgroundColor: myTablesColors.well,
			borderWidth: Math.max(1, s(1.5)),
			borderColor: myTablesColors.rim,
			overflow: 'hidden',
			alignItems: 'center',
			justifyContent: 'center',
		},
		nameSeat: {
			backgroundColor: myTablesColors.seat,
		},
		seatName: {
			fontFamily: 'Sofia Sans',
			fontWeight: '700',
			fontSize: s(9),
			lineHeight: s(10),
			textAlign: 'center',
			color: '#FFFFFF',
		},
		// The city art's landmark, not the plinth under it (same framing as the My Tables popup).
		seatImage: {
			position: 'absolute',
			width: s(SEAT * 1.3),
			height: s(SEAT * 1.3 * (4 / 3)),
			left: s(-SEAT * 0.15),
			top: s(-SEAT * 0.2),
		},
		emptyImage: {
			width: s(40),
			height: s(SEAT),
		},
		overflow: {
			backgroundColor: 'transparent',
		},
		overflowDashed: {
			borderStyle: 'dashed',
			borderColor: myTablesColors.rimDim,
		},
		overflowText: {
			fontFamily: 'Sofia Sans',
			fontWeight: '700',
			fontSize: s(13),
			color: myTablesColors.gold,
		},
		overflowTextDim: {
			color: myTablesColors.muted,
		},
		messageColumn: {
			flex: 1,
			marginHorizontal: s(23),
			alignItems: 'center',
		},
		messageRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(8),
			maxWidth: '100%',
		},
		activeDot: {
			width: s(10),
			height: s(10),
			borderRadius: s(5),
			backgroundColor: myTablesColors.green,
		},
		message: {
			flexShrink: 1,
			fontFamily: 'Sofia Sans',
			fontWeight: '600',
			fontSize: s(18),
			lineHeight: s(24),
			color: '#FFFFFF',
		},
		detail: {
			fontFamily: 'Sofia Sans',
			fontSize: s(14),
			lineHeight: s(18),
			color: myTablesColors.secondary,
		},
		viewButton: {
			width: s(BUTTON.width),
			height: s(BUTTON.height),
			borderRadius: s(10),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(8),
			boxShadow: `0 0 ${s(10)}px rgba(255,184,0,0.35)`,
		} as any,
		viewLabel: {
			fontFamily: 'Sofia Sans',
			fontWeight: '800',
			fontSize: s(15),
			letterSpacing: s(0.4),
			textTransform: 'uppercase',
			color: myTablesColors.onGold,
		},
	});

export default DesktopRecentTablesBar;
